// Meeting Reminder Service
// Checks for upcoming virtual room meetings and shows notifications 15 minutes before

import { PushNotificationService } from './pushNotifications';
import { loadScheduledSessions } from '@/lib/organizerRepository';

interface UpcomingMeeting {
  id: string;
  title: string;
  room: string;
  startTime: string;
  minutesUntil: number;
}

export class MeetingReminderService {
  private static lastNotifiedMeetings: Set<string> = new Set();
  private static checkInterval: NodeJS.Timeout | null = null;

  /**
   * Start checking for upcoming meetings (runs every 5 minutes)
   */
  static startMeetingChecker(userId: string): void {
    if (this.checkInterval) {
      console.log('[MeetingReminders] Already running');
      return;
    }

    console.log('[MeetingReminders] Starting meeting reminder checker');
    
    // Check immediately
    this.checkUpcomingMeetings(userId);
    
    // Then check every 5 minutes
    this.checkInterval = setInterval(() => {
      this.checkUpcomingMeetings(userId);
    }, 5 * 60 * 1000); // 5 minutes
  }

  /**
   * Stop checking for meetings
   */
  static stopMeetingChecker(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
      console.log('[MeetingReminders] Stopped meeting checker');
    }
  }

  /**
   * Check for meetings starting in next 15 minutes
   */
  static async checkUpcomingMeetings(userId: string): Promise<void> {
    try {
      console.log('[MeetingReminders] Checking upcoming meetings for user:', userId);
      
      // Load user's scheduled sessions
      const sessions = await loadScheduledSessions(userId);
      
      if (!sessions || sessions.length === 0) {
        console.log('[MeetingReminders] No scheduled sessions found');
        return;
      }

      const now = new Date();
      const fifteenMinutesFromNow = new Date(now.getTime() + 15 * 60 * 1000);
      const upcomingMeetings: UpcomingMeeting[] = [];

      // Check each scheduled session
      for (const session of sessions) {
        if (!session.date || !session.time) continue;
        
        // Parse session date/time
        const sessionDateTime = new Date(`${session.date}T${session.time}`);
        
        // Check if meeting starts within next 15 minutes
        if (sessionDateTime > now && sessionDateTime <= fifteenMinutesFromNow) {
          const minutesUntil = Math.round((sessionDateTime.getTime() - now.getTime()) / (1000 * 60));
          
          upcomingMeetings.push({
            id: session.id,
            title: session.title || 'Virtual Room Meeting',
            room: session.room || 'Virtual Room',
            startTime: session.time,
            minutesUntil,
          });
        }
      }

      console.log('[MeetingReminders] Found', upcomingMeetings.length, 'meetings starting soon');

      // Show notifications for upcoming meetings
      for (const meeting of upcomingMeetings) {
        const meetingKey = `${meeting.id}-${meeting.startTime}`;
        
        // Only notify once per meeting
        if (!this.lastNotifiedMeetings.has(meetingKey)) {
          this.showMeetingReminder(meeting);
          this.lastNotifiedMeetings.add(meetingKey);
        }
      }

      // Clean up old notified meetings (keep last 50)
      if (this.lastNotifiedMeetings.size > 50) {
        const itemsArray = Array.from(this.lastNotifiedMeetings);
        this.lastNotifiedMeetings = new Set(itemsArray.slice(-50));
      }

    } catch (error) {
      console.error('[MeetingReminders] Error checking upcoming meetings:', error);
    }
  }

  /**
   * Show notification for upcoming meeting
   */
  private static async showMeetingReminder(meeting: UpcomingMeeting): Promise<void> {
    const timeText = meeting.minutesUntil <= 1 
      ? 'starting now' 
      : `starting in ${meeting.minutesUntil} minutes`;
    
    // Use PERSISTENT notification for locked-screen support
    await PushNotificationService.showPersistentNotification(
      `🎥 Meeting Reminder`,
      {
        body: `${meeting.title} ${timeText}`,
        tag: `meeting-${meeting.id}`,
        requireInteraction: true,
        vibrate: [200, 100, 200, 100, 200],
        data: {
          type: 'meeting',
          meetingId: meeting.id,
          url: '/organizer'
        },
        actions: [
          { action: 'open', title: 'Join Now', icon: '/icon-192.svg' },
          { action: 'dismiss', title: 'Dismiss', icon: '/icon-192.svg' }
        ]
      }
    );

    console.log('[MeetingReminders] Showed reminder for:', meeting.title);
  }

  /**
   * Manually check for a specific user (useful for testing)
   */
  static async checkNow(userId: string): Promise<UpcomingMeeting[]> {
    const sessions = await loadScheduledSessions(userId);
    const now = new Date();
    const thirtyMinutesFromNow = new Date(now.getTime() + 30 * 60 * 1000);
    const upcomingMeetings: UpcomingMeeting[] = [];

    for (const session of sessions) {
      if (!session.date || !session.time) continue;
      
      const sessionDateTime = new Date(`${session.date}T${session.time}`);
      
      // Show meetings starting in next 30 minutes (for testing)
      if (sessionDateTime > now && sessionDateTime <= thirtyMinutesFromNow) {
        const minutesUntil = Math.round((sessionDateTime.getTime() - now.getTime()) / (1000 * 60));
        
        upcomingMeetings.push({
          id: session.id,
          title: session.title || 'Virtual Room Meeting',
          room: session.room || 'Virtual Room',
          startTime: session.time,
          minutesUntil,
        });
      }
    }

    return upcomingMeetings;
  }
}
